package dev.lingvo.site.ds

// This file did not move to `:kit` and should not.
//
// Word diffing is editor code: three editor files, `pages/Ds.kt` and the review lane read it, all
// of them in `:site`, and it declares no top-level CssStyle or Keyframes that a second application
// needs. Moving it would add five names to the design system's public API for no consumer.
//
// It keeps `package dev.lingvo.site.ds` on purpose. Kotlin resolves same-package declarations
// across a module boundary without an import, so `toSitePalette`, `LVisuallyHiddenStyle` and
// `SitePalette` still resolve — from `:kit`, where they now live, and all three are public.

import androidx.compose.runtime.Composable
import dev.lingvo.shared.Placeholders
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.dom.ElementBuilder
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import dev.lingvo.site.i18n.t

/**
 * Word-level diff, rendered so it reads correctly by eye **and** aloud.
 *
 * Promoted out of the editor because it had two implementations. The editor's used `<del>`/`<ins>`
 * with visually-hidden markers; the review lane had its own `DiffSpan`, which tinted every changed
 * word with the same `accentSubtle` background — so an addition and a deletion were indistinguishable
 * from each other, and a screen reader got exactly the run-on sentence described below. The review
 * lane is where somebody decides whether a translation ships, which made it the worse of the two
 * places to have the weaker version.
 *
 * The type, the algorithm and the rendering all live here together on purpose: a caller that has two
 * strings wants [LDiffText], a caller that aligns its own parts (plural forms) wants [LDiffParts], and
 * neither should have to reach into the editor's package to get a diff.
 */
internal enum class DiffKind { UNCHANGED, ADDED, REMOVED }

internal data class DiffPart(
    val text: String,
    val kind: DiffKind,
) {
    val changed: Boolean get() = kind != DiffKind.UNCHANGED
}

private val DeletionElement = ElementBuilder.createBuilder<HTMLElement>("del")
private val InsertionElement = ElementBuilder.createBuilder<HTMLElement>("ins")

/**
 * A diff that survives being read aloud.
 *
 * Both changed kinds used to be a bare `Span` carrying `aria-label="removed: …"`. ARIA prohibits an
 * author-supplied name on the `generic` role an unstyled span has, so that label was dropped from
 * the accessibility tree by every engine implementing the rule — and since the parts interleave in
 * reading order, what was left was the old and new wording spliced into one run-on sentence. A
 * cleared translation, whose parts are all `REMOVED`, was read back as though it were still the
 * current value: the most alarming edit there is, announced as no edit at all.
 *
 * `<del>` and `<ins>` put the meaning in the markup. The marker beside them is a real text node and
 * not an attribute, because `deletion` and `insertion` are name-prohibited too and anything hung on
 * an attribute would be discarded the same way. Clipped rather than hidden — `display: none` and
 * `visibility: hidden` take it out of the accessibility tree, which is the whole point of it.
 */
@Composable
internal fun LDiffText(reference: String, candidate: String) {
    LDiffParts(wordDiff(reference, candidate))
}

/** The rendering half of [LDiffText], for callers that align their own parts — see `pluralDiff`. */
@Composable
internal fun LDiffParts(parts: List<DiffPart>) {
    val palette = ColorMode.current.toSitePalette()
    parts.forEach { part ->
        when (part.kind) {
            DiffKind.UNCHANGED -> Text(part.text)

            DiffKind.ADDED -> TagElement(
                elementBuilder = InsertionElement,
                applyAttrs = Modifier.color(palette.accent).fontWeight(600).styleModifier {
                    property("text-decoration", "underline")
                    property("text-underline-offset", "3px")
                    property("margin-inline", CHANGED_RUN_GAP)
                }.toAttrs(),
                content = {
                    DiffMarker("added: ")
                    Text(part.text)
                },
            )

            // Struck through and muted rather than coloured: a deletion is context for reading the
            // addition next to it, not a second thing competing for attention. Colour alone would
            // also make the two kinds indistinguishable in greyscale.
            DiffKind.REMOVED -> TagElement(
                elementBuilder = DeletionElement,
                applyAttrs = Modifier.color(palette.textMuted).styleModifier {
                    property("text-decoration", "line-through")
                    property("margin-inline", CHANGED_RUN_GAP)
                }.toAttrs(),
                content = {
                    DiffMarker("removed: ")
                    Text(part.text)
                },
            )
        }
    }
}

/**
 * Breathing room around a changed run, so two of them cannot read as one word.
 *
 * Within one sentence the tokeniser keeps whitespace as its own unchanged part, so runs were
 * already separated and this changes almost nothing. Between two *different* strings — which is
 * what "Matched source" diffs, a translation-memory hit against the current source — there is no
 * whitespace to inherit: the deletion ended and the insertion began against it, and the pair
 * rendered as `No subscriptions yetSubscriptions`, one unreadable word.
 *
 * A margin rather than a space character: a space would be selected and copied as part of the text,
 * and this text is a source string somebody may well be copying.
 */
private const val CHANGED_RUN_GAP = "0.15em"

@Composable
private fun DiffMarker(label: String) {
    Span(LVisuallyHiddenStyle.toModifier().toAttrs()) { Text(label) }
}

/**
 * A real alignment of two versions, not a membership test.
 *
 * This was previously set membership against the reference's word set, which had three consequences
 * that all pointed the same way: a deleted word appeared in neither position and was simply not
 * drawn, a reordering produced two identical sets and rendered as no change at all, and a blank
 * candidate returned an empty list — so clearing a translation, the most alarming edit there is,
 * displayed as though nothing had happened.
 *
 * Tokens are compared exactly, including case and punctuation. In a history panel `Save` becoming
 * `save` is a real edit, and normalising it away would hide the kind of change a reviewer is
 * looking for.
 */
internal fun wordDiff(reference: String, candidate: String): List<DiffPart> {
    val left = diffTokens(reference)
    val right = diffTokens(candidate)
    if (left.isEmpty() && right.isEmpty()) return emptyList()
    if (left.size > DIFF_TOKEN_CEILING || right.size > DIFF_TOKEN_CEILING) {
        return buildList {
            if (reference.isNotEmpty()) add(DiffPart(reference, DiffKind.REMOVED))
            if (candidate.isNotEmpty()) add(DiffPart(candidate, DiffKind.ADDED))
        }
    }

    val rows = left.size
    val columns = right.size
    // Longest common subsequence, filled from the end so the walk below can go forwards and emit
    // parts in reading order rather than building them backwards and reversing.
    val lcs = Array(rows + 1) { IntArray(columns + 1) }
    for (i in rows - 1 downTo 0) {
        for (j in columns - 1 downTo 0) {
            lcs[i][j] = if (left[i] == right[j]) {
                lcs[i + 1][j + 1] + 1
            } else {
                maxOf(lcs[i + 1][j], lcs[i][j + 1])
            }
        }
    }

    val parts = mutableListOf<DiffPart>()
    fun emit(text: String, kind: DiffKind) {
        val last = parts.lastOrNull()
        // Merge runs so a five-word deletion is one span rather than nine.
        if (last != null && last.kind == kind) {
            parts[parts.lastIndex] = last.copy(text = last.text + text)
        } else {
            parts += DiffPart(text, kind)
        }
    }
    var i = 0
    var j = 0
    while (i < rows && j < columns) {
        when {
            left[i] == right[j] -> { emit(left[i], DiffKind.UNCHANGED); i++; j++ }
            lcs[i + 1][j] >= lcs[i][j + 1] -> { emit(left[i], DiffKind.REMOVED); i++ }
            else -> { emit(right[j], DiffKind.ADDED); j++ }
        }
    }
    while (i < rows) { emit(left[i], DiffKind.REMOVED); i++ }
    while (j < columns) { emit(right[j], DiffKind.ADDED); j++ }
    return parts
}

/** One category's word diff, kept apart from every other category's. */

private fun diffTokens(text: String): List<String> {
    val out = mutableListOf<String>()
    fun addPlain(segment: String) {
        if (segment.isEmpty()) return
        Regex("\\s+|\\S+").findAll(segment).forEach { out += it.value }
    }
    var cursor = 0
    Placeholders.extractAll(text).sortedBy { it.start }.forEach { placeholder ->
        if (placeholder.start < cursor) return@forEach
        if (placeholder.start > cursor) addPlain(text.substring(cursor, placeholder.start))
        out += text.substring(placeholder.start, placeholder.end)
        cursor = placeholder.end
    }
    if (cursor < text.length) addPlain(text.substring(cursor))
    return out
}

private const val DIFF_TOKEN_CEILING = 600
